import { Logger, Presenter, type Scope } from 'wdc-cube'
import type { MercadoVetPresenter } from './mv_presenter'
import MvEmpresasTablePresenter from './mv_presenter_empresas_table'
import { MvCnpjFilterPanePresenter } from './mv_presenter_filter_pane'
import MvMapPresenter from './mv_presenter_map'
import MvSearchBarPresenter from './mv_presenter_search_bar'
import { buildSummary } from '../the-consumer-market/tcm_filter_summary'
import { CnpjPageScope, EmpresasTableScope } from './mv_scopes'
import { MercadoVetService } from './mv_service'
import type { BuscaModo, CnpjPainelResponse, VetFilter } from './mv_types'
import { errorMessage, formatMes, normalizeWord } from './mv_utils'

const LOG = Logger.get('MV-Presenter-Cnpj')

const service = MercadoVetService.singleton()

export default class MvCnpjPresenter extends Presenter<CnpjPageScope, MercadoVetPresenter> {
    constructor(owner: MercadoVetPresenter) {
        super(owner, owner.scope.cnpj)

        const s = this.scope
        const children: Scope[] = [
            s.appliedFilters,
            s.totalEmpresas,
            s.totalAtivas,
            s.totalFuncionarios,
            s.secundarias,
            s.nuvem,
            s.mapa,
            s.mercadoEmpresas,
            s.mercadoAtivas,
            s.mercadoNovas,
            s.novasEmpresas
        ]
        for (const child of children) child.update = this.update

        this.filterPane = new MvCnpjFilterPanePresenter(this, s.filtros)
        this.searchBar = new MvSearchBarPresenter(this, s.busca, {
            placeholder: 'Pesquise por nome ou palavras em destaque',
            modo: 'nome',
            modos: [
                { value: 'nome', label: 'Nome' },
                { value: 'palavras', label: 'Palavras chave' },
                { value: 'ambos', label: 'Nome e palavras-chave' }
            ]
        })
        this.detalhamento = new MvEmpresasTablePresenter(this, s.detalhamento, 'resumo')
        this.mapaFerramentas = new MvMapPresenter(this, s.mapaFerramentas, this.filterPane.filterManager)
    }

    readonly filterPane: MvCnpjFilterPanePresenter
    readonly searchBar: MvSearchBarPresenter
    readonly detalhamento: MvEmpresasTablePresenter
    readonly mapaFerramentas: MvMapPresenter

    get app() {
        return this.owner.app
    }
    private __detalhar?: MvEmpresasTablePresenter

    // :: State

    private __initialized = false
    private __painelRequest = 0
    private __listagemRequest = 0
    private __detalharRequest = 0
    private __selecionado?: { id: string; cnpj: string; nome: string }
    private __busca: { texto: string; modo: BuscaModo } = { texto: '', modo: 'nome' }

    // :: Lifecycle

    async initialize() {
        const s = this.scope

        this.searchBar.initialize()

        s.totalEmpresas.caption = 'Empresas'
        s.totalEmpresas.captionHint = 'Empresas com atividade veterinária e site no Google'
        s.totalAtivas.caption = 'Empresas Ativas'
        s.totalAtivas.captionHint = 'Empresas com situação cadastral ativa'
        s.totalFuncionarios.caption = 'Funcionários'
        s.totalFuncionarios.captionHint = 'Soma dos funcionários das empresas'

        s.secundarias.caption = 'Atividades Secundárias'
        s.secundarias.onItemClicked = this.action(this.__handleSecundariaClicked)

        s.nuvem.caption = 'Nuvem de Palavras em Destaque'
        s.nuvem.onWordClicked = this.action(this.__handlePalavraClicked)

        s.mapa.caption = 'Geolocalização das Empresas'
        s.mapa.onPointClicked = this.action(this.__handlePontoClicked)
        this.mapaFerramentas.initialize()

        s.detalhamento.caption = 'Detalhamento das Empresas'
        s.detalhamento.sort = { field: 'funcionarios', desc: true }
        s.detalhamento.onExpandClick = this.action(this.__handleExpand)

        s.mercadoEmpresas.caption = 'Empresas'
        s.mercadoEmpresas.captionHint = 'Empresas com o CNAE 7500100 (atividades veterinárias)'
        s.mercadoAtivas.caption = 'Ativas'
        s.mercadoNovas.caption = 'Novas'
        s.mercadoNovas.captionHint = 'Empresas abertas no último mês da base'
        s.novasEmpresas.caption = 'Novas Empresas'

        await this.filterPane.initialize()
    }

    async activate() {
        if (!this.__initialized) {
            this.__initialized = true
            await this.refresh()
        }
    }

    // :: Filtro

    override release() {
        this.__detalhar?.release()
        this.detalhamento.release()
        this.mapaFerramentas.release()
        this.searchBar.release()
        this.filterPane.release()
        super.release()
    }

    buildFilter(): VetFilter {
        const filter: VetFilter = { cnaeOrdem: this.filterPane.cnaeOrdem() }

        if (this.__busca.texto) filter.busca = { ...this.__busca }

        const empresa = this.filterPane.buildEmpresa()
        if (Object.keys(empresa).length > 0) filter.empresa = empresa

        for (const [campo, valor] of Object.entries(this.filterPane.buildCampos())) {
            if (valor) (filter as Record<string, unknown>)[campo] = valor
        }

        return filter
    }

    onFiltersCleared() {
        this.__selecionado = undefined
        this.mapaFerramentas.clear()
    }

    async fetchData() {
        await this.refresh()
    }

    async onSavedFilterApplied() {
        this.__selecionado = undefined
        this.mapaFerramentas.plotFromFilterManager()
        await this.refresh()
    }

    async onBuscaChanged(texto: string, modo: string) {
        this.__busca = { texto, modo: (modo as BuscaModo) || 'nome' }
        await this.refresh()
    }

    async fetchListagem(table: EmpresasTableScope, page: number) {
        await this.__fetchListagem(table, this.buildFilter(), page)
    }

    async onEmpresaClicked(table: EmpresasTableScope, id: string) {
        if (table !== this.scope.detalhamento) {
            table.selectedId = id
            return
        }
        const s = this.scope
        if (this.__selecionado?.id === id) {
            this.__selecionado = undefined
            table.selectedId = ''
            s.mapa.selectedId = ''
        } else {
            const row = table.rows.find((r) => r.id === id)
            if (!row) return
            const cnpj = String(row.cnpj ?? '')
            this.__selecionado = { id, cnpj, nome: String(row.nome || cnpj) }
            table.selectedId = id
            s.mapa.selectedId = cnpj
        }
        this.__describeAppliedFilters()
        await this.__fetchPainel(this.__painelFilter(this.buildFilter()))
    }

    // :: Dados

    async refresh() {
        this.filterPane.publish()
        this.__describeAppliedFilters()
        const filter = this.buildFilter()
        await Promise.all([
            this.__fetchPainel(this.__painelFilter(filter)),
            this.__fetchListagem(this.scope.detalhamento, filter, 0)
        ])
        this.owner.onCnpjFilterChanged()
    }

    private __painelFilter(filter: VetFilter): VetFilter {
        return this.__selecionado ? { ...filter, selecionado: this.__selecionado.cnpj } : filter
    }

    private __describeAppliedFilters() {
        const filters = this.filterPane.describeAppliedFilters()
        const area = this.mapaFerramentas.hasFilter() && buildSummary('Área no mapa', ['Desenhada no mapa'])
        const selecao = this.__selecionado && buildSummary('Selecionado na tabela', [this.__selecionado.nome])
        if (area) filters.push(area)
        if (selecao) filters.push(selecao)
        this.scope.appliedFilters.filters = filters
    }

    private async __fetchPainel(filter: VetFilter) {
        const s = this.scope
        const requestId = ++this.__painelRequest
        const loadables = [
            s.totalEmpresas,
            s.totalAtivas,
            s.totalFuncionarios,
            s.secundarias,
            s.nuvem,
            s.mapa,
            s.mercadoEmpresas,
            s.mercadoAtivas,
            s.mercadoNovas,
            s.novasEmpresas
        ]
        for (const item of loadables) item.loaded = false

        try {
            const resp = await service.fetchCnpjPainel(filter)
            if (requestId !== this.__painelRequest) return
            s.erro = ''
            this.__applyPainel(resp)
        } catch (caught) {
            if (requestId !== this.__painelRequest) return
            LOG.error('fetchCnpjPainel', caught)
            s.erro = errorMessage(caught)
        } finally {
            if (requestId === this.__painelRequest) {
                for (const item of loadables) item.loaded = true
            }
        }
    }

    private __applyPainel(resp: CnpjPainelResponse) {
        const s = this.scope

        s.totalEmpresas.value = resp.empresas
        s.totalAtivas.value = resp.ativas
        s.totalFuncionarios.value = resp.funcionarios

        const secundarias = this.filterPane.secundariasSelecionadas()
        s.secundarias.dataset = resp.secundarias.map((row) => ({
            id: row.codigo,
            descricao: row.descricao,
            qtdEmpresas: row.empresas,
            qtdFuncionarios: row.funcionarios,
            selected: secundarias.has(row.codigo)
        }))

        const selecionadas = this.filterPane.palavrasSelecionadas()
        s.nuvem.words = resp.palavras.map((p) => ({
            texto: p.texto,
            peso: p.peso,
            selected: selecionadas.has(normalizeWord(p.texto))
        }))

        s.mapa.points = resp.mapa

        s.mercadoEmpresas.value = resp.mercado.empresas
        s.mercadoAtivas.value = resp.mercado.ativas
        s.mercadoNovas.value = resp.mercado.novas
        s.novasEmpresas.dataset = resp.mercado.serie.map((r) => [formatMes(r.mes), r.abertas, r.fechadas])

        this.owner.onSobreOsDados(resp.sobre)
    }

    private async __fetchListagem(table: EmpresasTableScope, filter: VetFilter, page: number) {
        const isDetalhar = table !== this.scope.detalhamento
        const requestId = isDetalhar ? ++this.__detalharRequest : ++this.__listagemRequest
        const isCurrent = () =>
            isDetalhar ? requestId === this.__detalharRequest : requestId === this.__listagemRequest

        table.loading = true
        try {
            const resp = await service.fetchCnpjListagem({
                filter,
                page,
                pageSize: table.pageSize,
                sort: table.sort,
                detalhada: isDetalhar
            })
            if (!isCurrent()) return
            table.page = page
            table.total = resp.total
            table.rows = resp.rows
        } catch (caught) {
            if (!isCurrent()) return
            LOG.error('fetchCnpjListagem', caught)
            this.scope.erro = errorMessage(caught)
        } finally {
            if (isCurrent()) {
                table.loading = false
                table.loaded = true
            }
        }
    }

    private __fecharDetalhar() {
        this.__detalharRequest++
        this.__detalhar?.release()
        this.__detalhar = undefined
        this.scope.detalhar = null
        this.owner.update()
    }

    // :: Handlers

    private async __handleSecundariaClicked(codigo: string) {
        this.filterPane.toggleSecundaria(codigo)
        await this.refresh()
    }

    private async __handlePalavraClicked(texto: string) {
        this.filterPane.togglePalavra(texto)
        await this.refresh()
    }

    private async __handlePontoClicked(cnpj: string) {
        const s = this.scope
        s.mapa.selectedId = cnpj
        const row = s.detalhamento.rows.find((r) => String(r.cnpj ?? '') === cnpj)
        if (row) s.detalhamento.selectedId = String(row.id)
    }

    private async __handleExpand() {
        const s = this.scope
        this.__fecharDetalhar()

        const ordem = this.filterPane.cnaeOrdem()
        const table = new EmpresasTableScope()
        table.caption =
            this.filterPane.secundariasSelecionadas().size > 0 || ordem === 'secundario'
                ? 'Detalhar Empresas - CNAE Secundário'
                : 'Detalhar Empresas - CNAE Primário'
        table.pageSize = 100
        table.sort = { field: 'funcionarios', desc: true }
        table.expanded = true
        table.onShrunkClick = this.action(this.__handleShrunk)
        this.__detalhar = new MvEmpresasTablePresenter(this, table, 'completa')

        s.detalhar = table
        this.owner.update()

        await this.__fetchListagem(table, this.buildFilter(), 0)
    }

    private async __handleShrunk() {
        this.__fecharDetalhar()
    }
}
